import React, { useEffect, useMemo, useState } from 'react';
import { Plural, Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Accordion, AlertBanner, Card, List, Tabs } from '@ygtechnologies/rubus-ui';
import { faImage, faTag } from '@fortawesome/pro-light-svg-icons';
import { IconName } from '@fortawesome/fontawesome-svg-core';

import ItemCategoryDisplay from '@/components/item-categories/ItemCategoryDisplay';
import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';
import { ProductThumbnails } from '@/seller/components/products/ProductThumbnails';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { useImageUrls } from '@/components/OptionConfigurator/useImageUrls';
import {
    ConfiguratorGroupDto,
    ConfiguratorOptionDto,
    ConfiguratorPresetDto,
    useStylePreviewQuery,
} from '@/services/configurator';

import { useOptionsData } from '../useOptionsData';
import { useOptionsScope } from '../OptionsScope';
import { getRoomDisplayName } from '../../utils/configuratorRooms';
import theme from './IncludedOptionsList.module.scss';

interface IncludedGroup {
    group: ConfiguratorGroupDto;
    options: ConfiguratorOptionDto[];
    defaultOption: ConfiguratorOptionDto;
}

interface Props {
    style: ConfiguratorPresetDto;
    deadlineId: number;
    roomId: number | null;
}

/**
 * Resolves which groups a style applies to and which option each group would
 * default to. Used by both the apply and details dialogs.
 *
 * Sourced from the backend style preview (the configurator state as-if the style
 * were picked), so prices reflect what the buyer would actually get — e.g. a
 * styleOptionsFree default shows 0 even when another style is currently picked.
 */
function useIncludedGroups(
    style: ConfiguratorPresetDto,
    deadlineId: number,
    roomId: number | null
): { includedGroups: IncludedGroup[]; isLoading: boolean; isError: boolean } {
    const { residentialUnitId } = useOptionsScope();
    const { data: preview, isLoading, isError } = useStylePreviewQuery(residentialUnitId, style.id, roomId);

    const includedGroups = useMemo<IncludedGroup[]>(() => {
        const result: IncludedGroup[] = [];
        const seen = new Set<number>();
        for (const group of preview?.groups ?? []) {
            if (group.deadlineId !== deadlineId) continue;
            if (roomId != null && group.roomId !== roomId) continue;
            if (seen.has(group.id)) continue;
            const memberOpts = group.options.filter((opt) => opt.presets.some((p) => p.presetId === style.id));
            if (memberOpts.length === 0) continue;
            const defaultOpt = memberOpts.find((opt) => opt.presets.some((p) => p.presetId === style.id && p.isDefault));
            // A preset entry without a default is malformed — fall back to the
            // first member so we render something rather than crashing.
            const resolvedDefault = defaultOpt ?? memberOpts[0];
            seen.add(group.id);
            result.push({ group, options: memberOpts, defaultOption: resolvedDefault });
        }
        return result;
    }, [style.id, preview, deadlineId, roomId]);

    return { includedGroups, isLoading, isError };
}

interface GroupCardProps {
    includedGroup: IncludedGroup;
}

const IncludedGroupCard: React.FC<GroupCardProps> = ({ includedGroup }) => {
    // Showroom-aware image URLs: anonymous public URLs in the showroom, auth-gated otherwise.
    const { getProjectCatalogProductFileThumbnailUrl, getProjectCatalogProductFileUrl } = useImageUrls();
    const { group, options, defaultOption } = includedGroup;
    const hasAlternates = options.length > 1;
    const alternates = options.filter((opt) => opt.id !== defaultOption.id);

    const selectedRow = (
        <div className={theme.includedRow}>
            <ProductThumbnails
                images={defaultOption.product?.images ?? []}
                thumbnailUrlGenerator={getProjectCatalogProductFileThumbnailUrl}
                imageUrlGenerator={getProjectCatalogProductFileUrl}
                className={theme.includedThumb}
                disableOnClick
            />
            <div className={theme.includedMain}>
                <strong>{group.name}</strong>
                <div className={theme.includedCategory}>
                    <small>
                        <ItemCategoryDisplay value={defaultOption.product?.itemCategoryId ?? 0} />
                    </small>
                </div>
                <span className={theme.includedName}>{defaultOption.productName}</span>
                {defaultOption.product?.description && (
                    <div className={theme.includedDescription}>{defaultOption.product.description}</div>
                )}
                {hasAlternates && (
                    <small className={theme.alternatesHint}>
                        <Plural value={alternates.length} one="# more option" other="# more options" />
                    </small>
                )}
            </div>
            {!group.hidePrice && defaultOption.price && (
                <div className={theme.includedPriceColumn}>
                    <div className={theme.includedPrice}>
                        <BuyerCurrencyDisplay value={defaultOption.price.finalPrice} alwaysShowSign />
                    </div>
                </div>
            )}
        </div>
    );

    // A group with a single option has no choice to reveal — render it as a plain bordered
    // shell (no chevron), so it lines up with the expandable ones but reads as non-interactive.
    if (!hasAlternates) {
        return (
            <Card variant="flat" className={theme.groupRowCard}>
                {selectedRow}
            </Card>
        );
    }

    return (
        <Accordion variant="bordered" chevronPosition="right" title={<div className={theme.summaryRow}>{selectedRow}</div>}>
            <List variant="plain" className={theme.alternateList}>
                {alternates.map((opt) => {
                    const altThumb = opt.product?.images?.[0]
                        ? getProjectCatalogProductFileThumbnailUrl(opt.product.images[0].id)
                        : undefined;
                    return (
                        <List.Item key={opt.id}>
                            <List.Thumbnail src={altThumb} icon={faImage} size="lg" alt={opt.productName} />
                            <List.Content title={opt.productName}>
                                {opt.product?.description && (
                                    <span className={theme.includedDescription}>{opt.product.description}</span>
                                )}
                            </List.Content>
                            <List.Trailing>
                                {!group.hidePrice && opt.price && (
                                    <BuyerCurrencyDisplay value={opt.price.finalPrice} alwaysShowSign />
                                )}
                            </List.Trailing>
                        </List.Item>
                    );
                })}
            </List>
        </Accordion>
    );
};

const ALL_TAB = 'all';

interface FacetTab {
    /** `room-<id>` or `tag-<id>`; matched against the active-tab state. */
    key: string;
    label: string;
    /** Room icon name; null falls back to the tag icon. */
    icon: IconName | null;
}

/**
 * Builds a single tab strip that mashes rooms and tags together: one tab per room
 * that has included groups (ordered by the room list's sortOrder, General last),
 * then one tab per distinct tag (ordered by the project-defined tag sortOrder from
 * Settings > Tags). A group can appear under its room tab and under each of its tag tabs.
 */
function useFacetTabs(includedGroups: IncludedGroup[]): FacetTab[] {
    const { rooms } = useOptionsData();

    return useMemo<FacetTab[]>(() => {
        const presentRoomIds = new Set(includedGroups.map((ig) => ig.group.roomId));
        const roomTabs: FacetTab[] = (rooms ?? [])
            .filter((room) => presentRoomIds.has(room.roomId))
            .map((room) => ({ key: `room-${room.roomId}`, label: getRoomDisplayName(room), icon: room.icon }));

        const tagsById = new Map<number, { name: string; sortOrder: number }>();
        for (const { group } of includedGroups) {
            for (const tag of group.tags) {
                if (!tagsById.has(tag.id)) tagsById.set(tag.id, { name: tag.name, sortOrder: tag.sortOrder });
            }
        }
        const tagTabs: FacetTab[] = Array.from(tagsById, ([id, { name, sortOrder }]) => ({ id, name, sortOrder }))
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(({ id, name }) => ({ key: `tag-${id}`, label: name, icon: null }));

        return [...roomTabs, ...tagTabs];
    }, [includedGroups, rooms]);
}

function matchesTab(group: ConfiguratorGroupDto, tabKey: string): boolean {
    const [kind, idStr] = tabKey.split('-');
    const id = Number(idStr);
    if (kind === 'room') return group.roomId === id;
    return group.tags.some((tag) => tag.id === id);
}

export const IncludedOptionsList: React.FC<Props> = ({ style, deadlineId, roomId }) => {
    const { includedGroups, isLoading, isError } = useIncludedGroups(style, deadlineId, roomId);
    const tabs = useFacetTabs(includedGroups);
    const [activeTab, setActiveTab] = useState(ALL_TAB);

    // A style with a single facet doesn't need tabs — keep it flat.
    const showTabs = tabs.length > 1;

    // Reset to "all" if the active tab disappears (e.g. switching style in the dialog).
    useEffect(() => {
        if (activeTab !== ALL_TAB && !tabs.some((tab) => tab.key === activeTab)) {
            setActiveTab(ALL_TAB);
        }
    }, [tabs, activeTab]);

    const visibleGroups = useMemo(() => {
        if (!showTabs || activeTab === ALL_TAB) return includedGroups;
        return includedGroups.filter((ig) => matchesTab(ig.group, activeTab));
    }, [includedGroups, activeTab, showTabs]);

    if (isLoading) {
        return (
            <div className={theme.includedSection}>
                <LoadingIndicator />
            </div>
        );
    }

    // A failed preview must look different from "this style includes nothing" — the buyer is
    // about to apply the style based on this list. Never fall back to current-state prices,
    // those are wrong for a non-picked style.
    if (isError) {
        return (
            <div className={theme.includedSection}>
                <AlertBanner type="danger" title={<Trans>Could not load the options in this style</Trans>}>
                    <Trans>Something went wrong while loading the style preview.</Trans>
                </AlertBanner>
            </div>
        );
    }

    if (includedGroups.length === 0) return null;

    return (
        <div className={theme.includedSection}>
            {showTabs && (
                <Tabs
                    activeId={activeTab}
                    onChange={setActiveTab}
                    variant="pill"
                    collapseToDropdownOnMobile
                    className={theme.facetTabs}>
                    {tabs.map((tab) => (
                        <Tabs.Tab key={tab.key} id={tab.key} label={tab.label} icon={tab.icon ? ['fal', tab.icon] : faTag} />
                    ))}
                    <Tabs.Tab id={ALL_TAB} label={t`All options`} />
                </Tabs>
            )}
            <div className={theme.includedList}>
                {visibleGroups.map((ig) => (
                    <IncludedGroupCard key={ig.group.id} includedGroup={ig} />
                ))}
            </div>
        </div>
    );
};
